import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, TrendingUp, FileCheck, Leaf, Award, MapPin,
  Shield, Clock, CheckCircle2, Building2, Users, FileText,
  AlertTriangle, Download, Lock, Briefcase, Target, Zap,
  Share2, Sparkles, Link2, FileSpreadsheet, Wallet, BarChart3,
  Truck, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const HomePage = () => {
  // State emission factors for hero tile
  const stateFactors = [
    { state: 'QLD', name: 'Queensland', factor: 0.67, color: '#E76F51' },
    { state: 'NSW', name: 'New South Wales', factor: 0.64, color: '#E9C46A' },
    { state: 'VIC', name: 'Victoria', factor: 0.78, color: '#2A9D8F' },
    { state: 'SA', name: 'South Australia', factor: 0.22, color: '#74A57F' },
    { state: 'WA', name: 'Western Australia', factor: 0.50, color: '#264653' },
    { state: 'TAS', name: 'Tasmania', factor: 0.20, color: '#003B5C' },
    { state: 'NT', name: 'Northern Territory', factor: 0.56, color: '#F4A261' },
    { state: 'ACT', name: 'ACT', factor: 0.64, color: '#94A3B8' }
  ];

  // Why SMEs are investing in emission tracking
  const whyTrackEmissions = [
    {
      icon: Truck,
      title: 'Supply Chain Demands',
      description: 'Large corporations now require suppliers to provide accurate emission data for their Scope 3 reporting. Be ready when they ask.',
      highlight: true
    },
    {
      icon: Award,
      title: 'Win More Tenders',
      description: 'Government and corporate tenders increasingly require environmental disclosures. Stand out with verified emissions data.',
      highlight: false
    },
    {
      icon: Wallet,
      title: 'Access to Finance',
      description: 'Banks and investors consider ESG factors when providing loans or insurance. Demonstrate your environmental credentials.',
      highlight: false
    },
    {
      icon: BarChart3,
      title: 'Cut Energy Costs',
      description: 'Measuring emissions reveals energy wastage. Most businesses find cost savings on their energy bills.',
      highlight: false
    }
  ];

  const platformFeatures = [
    {
      icon: FileCheck,
      title: 'Grant & Tender Ready Reports',
      description: 'Professional PDF exports formatted for government procurement and grant applications. One-click generation.',
      highlight: true
    },
    {
      icon: Shield,
      title: 'Evidence Vault',
      description: 'Attach invoices, utility bills, and receipts to every entry. Defensible records for auditors and stakeholders.',
      highlight: false
    },
    {
      icon: Sparkles,
      title: 'AI-Powered Report Templates',
      description: 'Built-in templates with AI-assisted wording suggestions. Professional reports without the consultant fees.',
      highlight: false
    },
    {
      icon: Link2,
      title: 'Shareable Verification URL',
      description: 'Share your "Green Passport" URL with customers, partners, or tender committees. Real-time verified data.',
      highlight: false
    }
  ];

  const trustIndicators = [
    { value: 'NGA', label: 'Factors Updated Yearly' },
    { value: '8', label: 'States & Territories' },
    { value: 'AASB S2', label: 'Aligned' },
    { value: '24/7', label: 'Secure Access' }
  ];

  const consultantBenefits = [
    {
      icon: Users,
      title: 'Multi-Client Dashboard',
      description: 'Manage unlimited organisations from a single login. Each client gets their own secure workspace.'
    },
    {
      icon: Download,
      title: 'White-Label Reports',
      description: 'Export professional PDFs branded with your client\'s logo. Present as your own deliverables.',
      comingSoon: true
    },
    {
      icon: Zap,
      title: '20% Multi-Org Discount',
      description: 'Add client organisations at $17.60/month (Pro) or $8.80/month (Standard). Volume pricing built-in.'
    },
    {
      icon: Lock,
      title: 'Client Data Isolation',
      description: 'Complete separation between clients. Secure, compliant, and audit-trail protected.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - SME Focused */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Brand Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm font-medium tracking-wide uppercase text-[#74A57F] bg-[#74A57F]/10 px-4 py-2 rounded-full" data-testid="hero-badge">
                  Emission Tracking for Australian SMEs
                </span>
                <span className="inline-flex items-center text-sm font-medium text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full">
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  NGA Factors Auto-Applied
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-headline">
                <span className="text-[#003B5C]">Clear Skies.</span>
                <br />
                <span className="text-[#74A57F]">Greener Bottom Lines.</span>
              </h1>

              <p className="text-lg lg:text-xl leading-relaxed text-slate-600 mb-6" data-testid="hero-description">
                Simple Scope 1 & 2 emission tracking for Australian SMEs. Generate reports for grant applications, respond to value chain requests, and prepare for future compliance—all in one secure platform.
              </p>

              {/* Core Value Proposition - What You Can Do */}
              <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-[#74A57F]" />
                  <span className="font-semibold text-[#003B5C]">Scope 1 & 2 Emissions Made Simple</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-xs text-orange-600 font-medium mb-1">Scope 1 - Direct</div>
                    <div className="text-sm text-slate-600">Petrol • Diesel • Gas</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <div className="text-xs text-amber-600 font-medium mb-1">Scope 2 - Electricity</div>
                    <div className="text-sm text-slate-600">Grid Power • GreenPower</div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center text-xs text-slate-500">
                    <Share2 className="w-3.5 h-3.5 mr-1 text-[#74A57F]" />
                    Share via URL
                  </div>
                  <div className="flex items-center text-xs text-slate-500">
                    <FileText className="w-3.5 h-3.5 mr-1 text-[#74A57F]" />
                    PDF Reports
                  </div>
                  <div className="flex items-center text-xs text-slate-500">
                    <Shield className="w-3.5 h-3.5 mr-1 text-[#74A57F]" />
                    Evidence Vault
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button
                    className="bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
                    data-testid="cta-get-started"
                  >
                    Start Free Demo
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <Link to="/login">
                  <Button
                    variant="outline"
                    className="bg-white text-[#003B5C] border border-[#003B5C]/20 hover:border-[#003B5C] rounded-full px-8 py-6 text-lg font-medium transition-all"
                    data-testid="cta-member-login"
                  >
                    Member Login
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Emission Factors Data Tile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgb(0,59,92,0.15)] overflow-hidden border border-slate-200" data-testid="hero-data-tile">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#003B5C] to-[#005580] px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Zap className="w-5 h-5 text-[#E9C46A]" />
                      <h3 className="text-white font-semibold">Electricity Emission Factors</h3>
                    </div>
                    <span className="text-xs text-white/70 bg-white/10 px-2 py-1 rounded">NGA Factors</span>
                  </div>
                </div>

                {/* State Grid */}
                <div className="p-5">
                  <div className="grid grid-cols-4 gap-3">
                    {stateFactors.map((s) => (
                      <div
                        key={s.state}
                        className="bg-slate-50 rounded-xl p-3 text-center hover:shadow-md transition-all hover:-translate-y-0.5 cursor-default"
                      >
                        <div
                          className="w-9 h-9 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-xs shadow-sm"
                          style={{ backgroundColor: s.color }}
                        >
                          {s.state}
                        </div>
                        <div className="text-xl font-bold text-[#003B5C]">{s.factor}</div>
                        <div className="text-[10px] text-slate-500">kg CO₂-e/kWh</div>
                      </div>
                    ))}
                  </div>

                  {/* Legend/Insight */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-[#003B5C] mr-1.5"></div>
                          <span>Cleanest: TAS (0.20)</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-[#2A9D8F] mr-1.5"></div>
                          <span>Highest: VIC (0.78)</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">
                      Factors applied by financial year for accurate reporting
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-3 -right-3 bg-[#74A57F] text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
                <MapPin className="w-3 h-3 inline mr-1" />
                All 8 States & Territories
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why SMEs Are Tracking Emissions Section */}
      <section className="py-20 bg-white" data-testid="why-track-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-medium rounded-full mb-4">
              The Business Case
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Australian SMEs Are Tracking Emissions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Even without mandatory reporting requirements, forward-thinking businesses are getting ahead of the curve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyTrackEmissions.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 border transition-all hover:shadow-lg ${reason.highlight
                      ? 'bg-[#003B5C] text-white border-[#003B5C]'
                      : 'bg-white border-slate-200 hover:border-[#74A57F]'
                    }`}
                  data-testid={`why-card-${index}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${reason.highlight ? 'bg-white/20' : 'bg-gradient-to-br from-[#003B5C] to-[#74A57F]'
                    }`}>
                    <Icon className={`w-6 h-6 ${reason.highlight ? 'text-white' : 'text-white'}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${reason.highlight ? 'text-white' : 'text-[#003B5C]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {reason.title}
                  </h3>
                  <p className={reason.highlight ? 'text-white/80' : 'text-slate-600'}>
                    {reason.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Chain / Supply Chain CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#003B5C] to-[#005580]" data-testid="value-chain-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center">
                <Globe className="w-10 h-10 text-[#74A57F]" />
              </div>
            </div>
            <div className="flex-grow text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Supplying to Larger Organisations?
              </h3>
              <p className="text-white/80">
                Large corporations reporting Scope 3 emissions need supplier data. Be ready when your customers ask for your carbon footprint. Share your emissions via a secure URL or PDF report.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link to="/signup">
                <Button className="bg-[#74A57F] text-white hover:bg-[#5F8575] px-6 py-3 rounded-lg font-medium">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 bg-gradient-to-br from-[#F8F9FA] to-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#74A57F]/10 text-[#74A57F] text-sm font-medium rounded-full mb-4">
              Platform Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need to Track & Share
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional reporting tools without the consultant price tag.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 border transition-all hover:shadow-lg ${feature.highlight
                      ? 'bg-[#003B5C] text-white border-[#003B5C]'
                      : 'bg-white border-slate-200 hover:border-[#74A57F]'
                    }`}
                  data-testid={`feature-card-${index}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feature.highlight ? 'bg-white/20' : 'bg-gradient-to-br from-[#003B5C] to-[#74A57F]'
                    }`}>
                    <Icon className={`w-6 h-6 ${feature.highlight ? 'text-white' : 'text-white'}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${feature.highlight ? 'text-white' : 'text-[#003B5C]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className={feature.highlight ? 'text-white/80' : 'text-slate-600'}>
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Future Scope 3 Note */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">Future-Ready Platform</h4>
                <p className="text-sm text-blue-700">
                  Lucent Sky ESG is designed to simplify Scope 1 & 2 tracking for SMEs today, while building the foundational data history needed for alignment with AASB S2. As standards evolve, we are enhancing the platform to support Group 3 organisations as they transition toward mandatory climate reporting in FY 2027/28.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Consultants Section */}
      <section className="py-20 bg-slate-900" data-testid="consultants-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center space-x-2 mb-6">
                <Briefcase className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 font-medium text-sm uppercase tracking-wide">
                  For ESG Consultants
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Scale Your Practice.
                <br />
                <span className="text-[#74A57F]">Not Your Overhead.</span>
              </h2>

              <p className="text-lg text-slate-300 mb-8">
                Managing ESG for multiple clients? Lucent Sky lets you add unlimited organisations under one account—with a 20% multi-organisation discount on every additional client.
              </p>

              <div className="space-y-4">
                {consultantBenefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#74A57F]/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#74A57F]" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-white">{benefit.title}</h4>
                          {benefit.comingSoon && (
                            <span className="text-[10px] font-medium bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/30">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/signup?plan=pro">
                  <Button className="bg-[#74A57F] text-white hover:bg-[#5F8575] px-8 py-4 text-lg font-semibold rounded-lg">
                    Start Managing Clients
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg font-medium rounded-lg">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Feature Highlights */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <Users className="w-8 h-8 text-[#74A57F] mb-3" />
                <div className="text-2xl font-bold text-white mb-1">Unlimited</div>
                <div className="text-slate-400 text-sm">Client Organisations</div>
              </div>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <Zap className="w-8 h-8 text-amber-400 mb-3" />
                <div className="text-2xl font-bold text-white mb-1">20% Off</div>
                <div className="text-slate-400 text-sm">Multi-Org Discount</div>
              </div>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <FileSpreadsheet className="w-8 h-8 text-cyan-400 mb-3" />
                <div className="text-2xl font-bold text-white mb-1">Bulk Import</div>
                <div className="text-slate-400 text-sm">CSV Data Upload</div>
              </div>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <Lock className="w-8 h-8 text-rose-400 mb-3" />
                <div className="text-2xl font-bold text-white mb-1">Isolated</div>
                <div className="text-slate-400 text-sm">Client Data</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Pricing Section */}
      <section className="py-20 bg-white" data-testid="pricing-preview-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003B5C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">
              Start free. Upgrade when you need audit-ready exports. All prices include GST.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Demo Sandbox */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-[#003B5C] mb-2">Demo Sandbox</h3>
                <div className="text-4xl font-bold text-[#003B5C] mb-1">$0</div>
                <p className="text-sm text-slate-500 mb-4">Try before you buy</p>
                <ul className="text-sm text-slate-600 space-y-2 mb-6 text-left">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> 5 emission entries</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> Sample PDF report</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> Dashboard access</li>
                </ul>
                <Link to="/signup?plan=demo">
                  <Button variant="outline" className="w-full border-[#003B5C] text-[#003B5C]">
                    Try Free
                  </Button>
                </Link>
              </div>
            </div>

            {/* Standard */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-[#003B5C] mb-2">Standard</h3>
                <div className="text-4xl font-bold text-[#003B5C] mb-1">$11<span className="text-lg font-normal">/mo</span></div>
                <p className="text-sm text-slate-500 mb-4">or $118.80/year (10% off)</p>
                <ul className="text-sm text-slate-600 space-y-2 mb-6 text-left">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> 10,000 entries</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> Single-click reporting</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> Standard email support</li>
                </ul>
                <Link to="/signup?plan=standard">
                  <Button variant="outline" className="w-full border-[#003B5C] text-[#003B5C]">
                    Get Standard
                  </Button>
                </Link>
              </div>
            </div>

            {/* Pro */}
            <div className="bg-[#003B5C] rounded-xl p-6 text-white shadow-xl relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#74A57F] text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Pro</h3>
                <div className="text-4xl font-bold mb-1">$22<span className="text-lg font-normal">/mo</span></div>
                <p className="text-sm text-white/70 mb-4">or $237.60/year (10% off)</p>
                <ul className="text-sm text-white/90 space-y-2 mb-6 text-left">
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-[#74A57F] mr-2" /> Full report templates</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-[#74A57F] mr-2" /> AI Co-Pilot narratives</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-[#74A57F] mr-2" /> Agent Co-Pilot assistant</li>
                  <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-[#74A57F] mr-2" /> Priority email support</li>
                </ul>
                <Link to="/signup?plan=pro">
                  <Button className="w-full bg-[#74A57F] hover:bg-[#5F8575] text-white">
                    Get Pro
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-8 space-y-2">
            <Link to="/pricing" className="text-[#74A57F] font-medium hover:underline block">
              View full feature comparison →
            </Link>
            <p className="text-sm text-slate-500">
              Need to pause? Account freeze available at $4/month (storage under 10GB)
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#003B5C] to-[#002A42]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Track Your Carbon Footprint?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join Australian SMEs who are getting ahead of value chain demands and future compliance requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button
                className="bg-[#74A57F] text-white hover:bg-[#5F8575] rounded-lg px-10 py-6 text-lg font-semibold shadow-xl"
                data-testid="final-cta"
              >
                Start Free Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="outline"
                className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-lg px-10 py-6 text-lg font-medium"
              >
                Member Login
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
